import React, { useRef, useLayoutEffect, useMemo } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Img1 from '../assets/createur_experience_1.png';
import Img2 from '../assets/createur_experience_2.png';
import Img3 from '../assets/createur_experience_3.png';
import Img4 from '../assets/createur_experience_4.png';
import Img5 from '../assets/createur_experience_5.png';
import Img6 from '../assets/createur_experience_6.png';
import Img7 from '../assets/createur_experience_7.png';
import Img8 from '../assets/createur_experience_8.png';
import Img9 from '../assets/createur_experience_9.png';
import Img10 from '../assets/createur_experience_10.png';
import Img11 from '../assets/createur_experience_11.png';

gsap.registerPlugin(ScrollTrigger);

const Content = styled.div`
  position: relative;
  flex: 1;
  min-height: 100vh;
  padding: 0;
  background-color: rgba(102, 15, 19, 1);
`;

const Mask = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
`;

const Stack = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh * 8);
  height: calc(100dvh * 8);
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  
`;

const Group = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100dvh;
`;

const FloatingImage = styled.img<{ position: number }>`
  position: absolute;
  object-fit: cover;   
  object-position: center;    
  border-radius: 8px;
  
  /* All images same size */
  width: 300px;
  height: 300px;
  
  ${({ position }) => {
    const positions = [
      { top: '2%', left: '5%' },   // 0
      { top: '6%', right: '10%' },  // 1
      { bottom: '2%', left: '15%' },// 2
      { bottom: '2%', right: '5%' } // 3
    ];
    const pos = positions[position % positions.length];
    return pos;
  }}
  
  z-index: ${({ position }) => 80 + (position % 4) * 10};
  
  @media (max-width: 900px) {
    width: clamp(100px, 25vw, 150px);
    height: clamp(100px, 25vw, 150px);
  }
  
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const TextOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  pointer-events: none;
  z-index: 100;
  width: 90%;
  text-align: center;
  will-change: transform;
  -webkit-font-smoothing: antialiased;
`;

const Title = styled.p`
  font-size: 8rem;
  color: #fff;
  font-family: 'MongoGrotesqueLight';
  margin: 0;
  line-height: 0.88;
  @media (max-width: 900px) {
    font-size: 5rem;
  }
  @media (max-width: 410px) {
    font-size: 4rem;
  }
`;

const SubtitleWrapper = styled.div`
  overflow: hidden;
  height: 50px;
  margin-top: 1rem;

  @media only screen and (min-device-width: 760px)and (max-device-width: 1024px) {
    height: 50px;
}
  @media (max-width: 760px) {
    height: 80px;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #ddd;
  font-family: 'PoppinsMedium';
  margin: 0;
`;

const Experience: React.FC = () => {
  const maskRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  
  // Fisher-Yates shuffle to select 8 unique images out of 11
  const selectedImages = useMemo(() => {
    const allImages = [Img1, Img2, Img3, Img4, Img5, Img6, Img7, Img8, Img9, Img10, Img11];
    const shuffled = [...allImages];
    
    // Shuffle the array
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 8);
  }, []);

  // Create frames of 4 images each so the first view shows 4 images
  const imageGroups = useMemo(() => {
    const out: string[][] = [] as unknown as string[][];
    for (let i = 0; i < selectedImages.length; i += 4) {
      out.push(selectedImages.slice(i, i + 4));
    }
    return out;
  }, [selectedImages]);

  useLayoutEffect(() => {
    if (!maskRef.current || !stackRef.current) return;

    const setVh = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      );
    };
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    setVh();

    if (ScrollTrigger.isTouch) {
      ScrollTrigger.normalizeScroll(true);
      ScrollTrigger.config({ ignoreMobileResize: true });
    }

    const ctx = gsap.context(() => {
      const maskEl = maskRef.current!;
      const stackEl = stackRef.current!;
      const groupCount = stackEl.children.length;

      if (subtitleRef.current) {
        gsap.fromTo(
          subtitleRef.current,
          { yPercent: 100 },
          {
            yPercent: 0,
            duration: 0.8,
            ease: 'power4.out',
            scrollTrigger: {
              trigger: maskEl,
              start: 'top 70%',
              once: true,
            },
          }
        );
      }

      ScrollTrigger.batch(maskEl.querySelectorAll('img'), {
        once: true,
        onEnter: () => ScrollTrigger.refresh(),
      });

      gsap
        .timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: maskEl,
            start: 'top top',
            end: () => `+=${(groupCount - 1) * maskEl.offsetHeight}`,
            scrub: 0.1,
            pin: true,
            pinType: /iP/.test(navigator.platform) ? 'fixed' : 'transform',
            pinSpacing: true,
            invalidateOnRefresh: true,
          },
        })
        .to(stackEl, {
          y: () => -(groupCount - 1) * maskEl.offsetHeight,
          force3D: true,
          modifiers: {
            y: (val) => Math.round(parseFloat(val)) + 'px',
          },
        });
    });

    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
      ctx.revert();
    };
  }, [selectedImages]);

  return (
    <Content>
      <Mask ref={maskRef}>
        <Stack ref={stackRef} style={{ height: `calc(100dvh * ${imageGroups.length})` }}>
          {imageGroups.map((group, gi) => (
            <Group key={gi}>
              {group.map((img, i) => (
                <FloatingImage
                  key={i}
                  src={img}
                  position={i}
                  alt={`Experience ${gi * 4 + i + 1}`}
                  decoding="async"
                />
              ))}
            </Group>
          ))}
        </Stack>
        <TextOverlay>
          <Title>
            CRÉATEUR <br /> D&apos;EXPÉRIENCES UNIQUES
          </Title>
          <SubtitleWrapper>
            <Subtitle ref={subtitleRef}>
              Expérience, pédagogie, fêtes, spectacle et innovation :<br />
              Voilà notre terrain de jeu.
            </Subtitle>
          </SubtitleWrapper>
        </TextOverlay>
      </Mask>
    </Content>
  );
};

export default Experience;
